import * as React from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { cn } from '../../lib/utils';
import { Button } from './button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './popover';

/**
 * Calendar component with exact Hyperkit styling
 *
 * Hyperkit Specs (Node ID: 374:4823):
 * - Uses react-day-picker
 * - Consistent with input styling
 * - Navigation arrows
 * - Selected date highlight
 */
const Calendar = React.forwardRef(
  ({ className, classNames, showOutsideDays = true, ...props }, ref) => {
    return (
      <DayPicker
        ref={ref}
        showOutsideDays={showOutsideDays}
        className={cn('p-3', className)}
        classNames={{
          months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
          month: 'space-y-4',
          caption: 'flex justify-center pt-1 relative items-center',
          caption_label: 'text-sm font-medium text-grey-950 dark:text-grey-50',
          nav: 'space-x-1 flex items-center',
          nav_button: cn(
            'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
            'inline-flex items-center justify-center rounded-md text-sm font-medium',
            'transition-colors hover:bg-black/5 focus-visible:outline-none',
            'disabled:pointer-events-none disabled:opacity-50',
            'dark:hover:bg-white/5'
          ),
          nav_button_previous: 'absolute left-1',
          nav_button_next: 'absolute right-1',
          table: 'w-full border-collapse space-y-1',
          head_row: 'flex',
          head_cell: 'text-grey-500 rounded-md w-8 font-normal text-[0.8rem] dark:text-grey-400',
          row: 'flex w-full mt-2',
          cell: cn(
            'relative p-0 text-center text-sm focus-within:relative focus-within:z-20',
            '[&:has([aria-selected])]:bg-grey-100 dark:[&:has([aria-selected])]:bg-grey-800',
            '[&:has([aria-selected].day-outside)]:bg-grey-100/50 dark:[&:has([aria-selected].day-outside)]:bg-grey-800/50',
            '[&:has([aria-selected].day-range-end)]:rounded-r-md'
          ),
          day: cn(
            'h-8 w-8 p-0 font-normal aria-selected:opacity-100',
            'inline-flex items-center justify-center rounded-md text-sm',
            'transition-colors hover:bg-black/5 focus-visible:outline-none',
            'dark:hover:bg-white/5'
          ),
          day_range_start: 'day-range-start',
          day_range_end: 'day-range-end',
          day_selected: cn(
            'bg-grey-950 text-white hover:bg-grey-900 hover:text-white',
            'focus:bg-grey-950 focus:text-white',
            'dark:bg-grey-50 dark:text-grey-950 dark:hover:bg-grey-100 dark:focus:bg-grey-50'
          ),
          day_today: 'bg-grey-100 text-grey-950 dark:bg-grey-800 dark:text-grey-50',
          day_outside: cn(
            'day-outside text-grey-400 opacity-50',
            'aria-selected:bg-grey-100/50 aria-selected:text-grey-500 aria-selected:opacity-30',
            'dark:text-grey-500 dark:aria-selected:bg-grey-800/50 dark:aria-selected:text-grey-400'
          ),
          day_disabled: 'text-grey-400 opacity-50 dark:text-grey-500',
          day_range_middle: cn(
            'aria-selected:bg-grey-100 aria-selected:text-grey-950',
            'dark:aria-selected:bg-grey-800 dark:aria-selected:text-grey-50'
          ),
          day_hidden: 'invisible',
          ...classNames,
        }}
        {...props}
      />
    );
  }
);
Calendar.displayName = 'Calendar';

/**
 * DatePicker - Combined input + calendar popover
 */
const DatePicker = React.forwardRef(
  (
    {
      className,
      value,
      onChange,
      placeholder = 'Pick a date',
      disabled,
      ...props
    },
    ref
  ) => {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant="secondary"
            className={cn(
              'w-full justify-start text-left font-normal',
              !value && 'text-grey-500',
              className
            )}
            disabled={disabled}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value ? format(value, 'PPP') : <span>{placeholder}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={value}
            onSelect={onChange}
            initialFocus
            {...props}
          />
        </PopoverContent>
      </Popover>
    );
  }
);
DatePicker.displayName = 'DatePicker';

/**
 * DateRangePicker - For selecting date ranges
 */
const DateRangePicker = React.forwardRef(
  (
    {
      className,
      value,
      onChange,
      placeholder = 'Pick a date range',
      disabled,
      ...props
    },
    ref
  ) => {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant="secondary"
            className={cn(
              'w-full justify-start text-left font-normal',
              !value && 'text-grey-500',
              className
            )}
            disabled={disabled}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value?.from ? (
              value.to ? (
                <>
                  {format(value.from, 'LLL dd, y')} - {format(value.to, 'LLL dd, y')}
                </>
              ) : (
                format(value.from, 'LLL dd, y')
              )
            ) : (
              <span>{placeholder}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            defaultMonth={value?.from}
            selected={value}
            onSelect={onChange}
            numberOfMonths={2}
            initialFocus
            {...props}
          />
        </PopoverContent>
      </Popover>
    );
  }
);
DateRangePicker.displayName = 'DateRangePicker';

export { Calendar, DatePicker, DateRangePicker };
